import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { isNetworkFailure } from '@/lib/network/apiErrors';
import { getNetworkState, onReconnect } from '@/lib/network/connectivity';

const POLL_MS = 8000;

export default function useBookingTracking(bookingId, enabled = true) {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkOffline, setNetworkOffline] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!bookingId || !enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      if (!getNetworkState().isOnline) {
        if (!cancelled) {
          setNetworkOffline(true);
          setLoading(false);
        }
        return;
      }

      try {
        const { data } = await api.get(`/bookings/${bookingId}/tracking`);
        if (!cancelled) {
          setTracking(data.tracking);
          setError(null);
          setNetworkOffline(false);
        }
      } catch (e) {
        if (!cancelled) {
          if (isNetworkFailure(e)) {
            setNetworkOffline(true);
            setError(null);
          } else {
            setError(e.response?.data?.detail || 'Could not load tracking');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    timerRef.current = setInterval(load, POLL_MS);
    const unsubReconnect = onReconnect(() => {
      if (!cancelled) load();
    });

    return () => {
      cancelled = true;
      unsubReconnect();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [bookingId, enabled]);

  return { tracking, loading, error, networkOffline };
}
